import React, { useState,useEffect } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Checkbox,
  Input,
} from "@chakra-ui/react";
import Select from "react-select";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

import {
  category,
  stringConditions,
  dropdownConditions,
} from "../utils/SearchPickList";

import { getShadeNumberList } from "../../../redux/reduxApi/admin/ShadeReduxApi";
import { setFilteredStatus } from "../../../redux/reduxSlice/admin/ShadesSlice";
import ProgressBar from "../utils/progressBar/ProgressBar";
import { getCategoryList } from "../../../redux/reduxApi/admin/CategoryReduxApi";

export default function ShadeSearchDrawer({
  isSearchDrawerOpen,
  closeDrawer,
  setRecordCount,
}) {
  const dispatch = useDispatch();
  const [showProgressBar, setShowProgressBar] = useState(false);

  /* -------------------- FILTER CONFIG -------------------- */
const filtersConfig = [
  { key: "shadeNo", label: "Shade Number", optionsType: "string" },
  { key: "shadeCategory", label: "Category", optionsType: "category" },
  { key: "fabric", label: "Fabric", optionsType: "string" },
  { key: "gsm", label: "GSM", optionsType: "number" },
  { key: "uvBlockage", label: "UV Blockage", optionsType: "string" },
  { key: "opennessFactor", label: "Openness Factor", optionsType: "string" },
  { key: "greenGuard", label: "Green Guard", optionsType: "string" },
];

const [categoryOptions, setCategoryOptions] = useState([]);

useEffect(() => {
  dispatch(getCategoryList())
    .then((res) => {
      const data = res.payload?.data || [];

      const formatted = data
        .filter((item) => item.parentCategoryId === null)
        .map((item) => ({
          label: item.name,
          value: item.ROWID,
        }));

      setCategoryOptions(formatted);
    })
    .catch((err) => {
      console.error("Category fetch error", err);
    });
}, [dispatch]);

  /* -------------------- STATE -------------------- */
  const [filters, setFilters] = useState(
    filtersConfig.reduce((acc, f) => {
      acc[f.key] = {
        checked: false,
        condition: { label: "Is", value: "is" },
        showInput: false,
        value: "",
      };
      return acc;
    }, {})
  );

  /* -------------------- HANDLERS -------------------- */

  const handleCheckboxChange = (key) => (e) => {
    const checked = e.target.checked;

    setFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        checked,
        condition: { label: "Is", value: "is" },
        showInput: checked,
        value: "",
      },
    }));
  };

  const handleConditionChange = (key) => (selected) => {
    const hideInputFor = ["is-empty", "is-not-empty"];

    setFilters((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        condition: selected,
        showInput: !hideInputFor.includes(selected.value),
      },
    }));
  };

  const handleValueChange = (key) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: { ...prev[key], value },
    }));
  };

  const getOptions = (type) => {
    switch (type) {
      case "category":
        return categoryOptions;
      default:
        return [];
    }
  };

  /* -------------------- SEARCH -------------------- */

  const handleSearch = async () => {
    const search = [];

    filtersConfig.forEach(({ key }) => {
      const current = filters[key];

      if (current.checked) {
        let value = current.value;
        let operation = current.condition?.value || "is";

        if (operation === "is-empty" || operation === "is-not-empty") {
          value = "";
        }

        const shouldAdd =
          operation === "is-empty" ||
          operation === "is-not-empty" ||
          (value !== undefined && value !== null && value !== "");

        if (shouldAdd) {
          search.push({
            field: key,
            operation,
            value,
          });
        }
      }
    });

    if (search.length === 0) {
      toast.warning("Please select at least one filter");
      return;
    }

    setShowProgressBar(true);

    try {
      const query = {
        page: "1",
        size: "100",
        search,
      };

      sessionStorage.setItem("shadeSearchQuery", JSON.stringify(query));

      const response = await dispatch(getShadeNumberList(query));
console.log("response", response)
      setShowProgressBar(false);

      if (
        response?.error?.code === "ERR_BAD_REQUEST" ||
        response?.error?.message?.includes("404")
      ) {
        toast.warning("No records found!");
      } else if (response?.payload?.data?.success) {
        const data = response.payload?.data?.shades || [];

        if (data.length > 0) {
          setRecordCount && setRecordCount(data.length);
          dispatch(setFilteredStatus(true));
          toast.success("Shades filtered successfully!");
        } else {
          toast.warning("No records found!");
          dispatch(setFilteredStatus(false));
        }

        closeDrawer();
      } else {
        toast.error("Error in filtering shades");
      }
    } catch (err) {
      setShowProgressBar(false);
      toast.error("Something went wrong");
    }
  };

  /* -------------------- RESET -------------------- */

  const handleReset = () => {
    setFilters(
      filtersConfig.reduce((acc, f) => {
        acc[f.key] = {
          checked: false,
          condition: { label: "Is", value: "is" },
          showInput: false,
          value: "",
        };
        return acc;
      }, {})
    );
  };

  const handleOnClose = () => {
    handleReset();
    closeDrawer();
  };

  /* -------------------- UI -------------------- */

  return (
    <Drawer
      isOpen={isSearchDrawerOpen}
      placement="right"
      size="xs"
      onClose={handleOnClose}
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />

        <DrawerHeader>
          <div className="d-flex gap-4">
            <span>Search Shade</span>
            <button className="reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </DrawerHeader>

        <DrawerBody>
          {showProgressBar && <ProgressBar colorScheme="pink" />}

          {filtersConfig.map(({ key, label, optionsType }) => {
            const current = filters[key];

            return (
              <div className="mt-3" key={key}>
                <Checkbox
                  isChecked={current.checked}
                  onChange={handleCheckboxChange(key)}
                >
                  {label}
                </Checkbox>

                {current.checked && (
                  <>
                    {/* Condition */}
                    <Select
                      className="mt-2"
                      options={
                        optionsType === "category"
                          ? dropdownConditions
                          : stringConditions
                      }
                      value={current.condition}
                      onChange={handleConditionChange(key)}
                    />

                    {/* Value */}
                    {current.showInput && (
                      <>
                        {optionsType === "category" ? (
                          <Select
                            isClearable
                            className="mt-2"
                            options={getOptions(optionsType)}
                            placeholder={`Select ${label}`}
                            onChange={(opt) =>
                              handleValueChange(key)(opt ? opt.value : "")
                            }
                            value={
                              getOptions(optionsType).find(
                                (o) => o.value === current.value
                              ) || null
                            }
                          />
                        ) : (
                          <Input
                            className="mt-2"
                            type={
                              optionsType === "number" ? "number" : "text"
                            }
                            placeholder={`Enter ${label}`}
                            value={current.value || ""}
                            onChange={(e) =>
                              handleValueChange(key)(e.target.value)
                            }
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </DrawerBody>

        <center>
          <DrawerFooter style={{ display: "inline-block" }}>
            <div className="d-flex gap-3">
              <button className="cancel-button" onClick={handleOnClose}>
                Cancel
              </button>
              <button className="main-btn" onClick={handleSearch}>
                <span className="inner-content">Search</span>
              </button>
            </div>
          </DrawerFooter>
        </center>
      </DrawerContent>
    </Drawer>
  );
}